import { Thread } from '@langchain/langgraph-sdk'
import { t } from '@lingui/macro'
import { format, formatDistanceToNow, isValid } from 'date-fns'
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'
import { useThreads } from '../providers/Thread'
import { X, Search } from 'lucide-react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    CircularProgress,
    Box,
    Chip,
    TextField,
    InputAdornment
} from '@mui/material'
import { getContentString } from '../lib/thread-utils'

interface Assistant {
    assistant_id: string
    name?: string
    [key: string]: any
}

interface ThreadHistoryModalProps {
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    assistants?: Assistant[]
}

export function ThreadHistoryModal({ isOpen, onOpenChange, assistants = [] }: ThreadHistoryModalProps) {
    const [, setThreadId] = useQueryParam('threadId', StringParam)
    const { getThreads, threads, setThreads } = useThreads()
    const [loading, setLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const searchInputRef = useRef<HTMLInputElement>(null)

    // Focus the search input when the modal opens with a more robust approach
    useEffect(() => {
        if (isOpen) {
            // Use multiple attempts with increasing delays to ensure focus works
            const attempts = [100, 300, 500]

            attempts.forEach((delay) => {
                const timer = setTimeout(() => {
                    if (searchInputRef.current) {
                        searchInputRef.current.focus()
                    }
                }, delay)

                return () => clearTimeout(timer)
            })
        }
    }, [isOpen])

    // Load threads when modal opens - always show all assistants
    useEffect(() => {
        if (isOpen) {
            console.log(t`Modal is open, loading all threads...`)
            setLoading(true)
            setSearchQuery('') // Reset search when opening

            getThreads()
                .then((threads) => {
                    console.log(t`Threads loaded: ${threads.length}`)
                    setThreads(threads)
                })
                .catch((error) => {
                    console.error(t`Error loading threads: ${error}`)
                })
                .finally(() => setLoading(false))
        }
    }, [isOpen, getThreads, setThreads])

    // Format date as relative time or absolute date
    const formatThreadDate = (date: Date | string | number | null | undefined) => {
        if (!date) return ''

        try {
            const threadDate = typeof date === 'string' ? new Date(date) : (date as Date)

            if (!isValid(threadDate)) return ''

            // For recent dates, show relative time (e.g., "35 mins ago")
            if (new Date().getTime() - threadDate.getTime() < 7 * 24 * 60 * 60 * 1000) {
                // Less than 7 days old - use relative time
                return formatDistanceToNow(threadDate, { addSuffix: true })
            } else {
                // Older than 7 days - use date format
                return format(threadDate, t`MMM d`)
            }
        } catch (error) {
            console.error(t`Error formatting date: ${error}`)
            return ''
        }
    }

    // Get friendly assistant name from ID
    const getFriendlyAssistantName = useCallback(
        (assistantId: string | unknown): string => {
            if (typeof assistantId !== 'string') {
                return t`Unknown Assistant`
            }
            // Look up the assistant name in the assistants array
            const assistant = assistants.find((a) => a.assistant_id === assistantId)
            return assistant?.name || assistantId.substring(0, 8)
        },
        [assistants]
    )

    // Get assistant name from thread
    const getAssistantName = useCallback(
        (thread: Thread) => {
            // Try to get assistant name from thread metadata
            if (thread.metadata && typeof thread.metadata === 'object') {
                if ('assistant_name' in thread.metadata && thread.metadata.assistant_name) {
                    return thread.metadata.assistant_name as string
                }

                if ('assistant_id' in thread.metadata && thread.metadata.assistant_id) {
                    // Look up friendly name from ID
                    return getFriendlyAssistantName(thread.metadata.assistant_id as string)
                }
            }

            // Fallback to assistant_id directly if present
            if ('assistant_id' in thread && thread.assistant_id) {
                return getFriendlyAssistantName(thread.assistant_id)
            }

            return t`Unknown Assistant`
        },
        [getFriendlyAssistantName]
    )

    // Get thread title
    const getThreadTitle = (thread: Thread): string => {
        // Try to get the first message content as the title
        if (
            typeof thread.values === 'object' &&
            thread.values &&
            'messages' in thread.values &&
            Array.isArray(thread.values.messages) &&
            thread.values.messages?.length > 0
        ) {
            const firstMessage = thread.values.messages[0]
            return getContentString(firstMessage.content)
        }
        return thread.thread_id
    }

    // Handle thread selection
    const handleThreadClick = (thread: Thread) => {
        setThreadId(thread.thread_id, 'pushIn')
        onOpenChange(false) // Close modal after selection
    }

    // Filter threads based on search query
    const filteredThreads = useMemo(() => {
        if (!searchQuery.trim()) return threads

        return threads.filter((thread) => {
            const title = getThreadTitle(thread).toLowerCase()
            const assistant = getAssistantName(thread).toLowerCase()
            const query = searchQuery.toLowerCase()

            return title.includes(query) || assistant.includes(query)
        })
    }, [threads, searchQuery, getAssistantName])

    return (
        <Dialog
            open={isOpen}
            onClose={() => onOpenChange(false)}
            maxWidth='md'
            fullWidth
            disableAutoFocus={true}
            disableEnforceFocus={true}
            PaperProps={{
                style: {
                    borderRadius: 8,
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                    height: '70vh',
                    maxHeight: '700px',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle
                sx={{
                    padding: '16px 24px',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                    flexWrap: 'nowrap',
                    gap: 2
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1
                    }}
                >
                    <TextField
                        placeholder={t`Search...`}
                        variant='standard'
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size='small'
                        inputRef={searchInputRef}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <Search size={16} />
                                </InputAdornment>
                            ),
                            disableUnderline: true,
                            sx: {
                                paddingLeft: 1,
                                '& .MuiInputBase-input': {
                                    padding: '6px 0'
                                }
                            }
                        }}
                        sx={{
                            borderRadius: 1,
                            '&:focus-within': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)'
                            }
                        }}
                    />
                </Box>

                <IconButton
                    aria-label='close'
                    onClick={() => onOpenChange(false)}
                    edge='end'
                    size='small'
                    sx={{
                        padding: '8px',
                        borderRadius: '50%',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                    }}
                >
                    <X size={18} />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    padding: 0,
                    flexGrow: 1,
                    overflow: 'hidden', // Prevent double scrollbars
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%'
                        }}
                    >
                        <CircularProgress size={40} />
                    </Box>
                ) : filteredThreads.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: 'text.secondary',
                            flexDirection: 'column',
                            gap: 1,
                            p: 4
                        }}
                    >
                        {threads.length === 0 ? (
                            <Typography variant='body1'>{t`No previous conversations found`}</Typography>
                        ) : (
                            <>
                                <Typography variant='body1'>{t`No results found for "${searchQuery}"`}</Typography>
                                <Typography variant='body2' color='text.secondary'>
                                    {t`Try a different search term`}
                                </Typography>
                            </>
                        )}
                    </Box>
                ) : (
                    <List
                        sx={{
                            width: '100%',
                            height: '100%',
                            padding: '12px 16px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '6px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '3px'
                            }
                        }}
                    >
                        {filteredThreads.map((thread) => {
                            // Extract the thread title and other info
                            const threadTitle = getThreadTitle(thread)
                            let threadDate: Date | undefined
                            const assistantName = getAssistantName(thread)

                            // Try to get the creation date
                            if (thread.created_at) {
                                threadDate = new Date(thread.created_at)
                            }

                            return (
                                <ListItem key={thread.thread_id} disablePadding sx={{ mb: 0.75 }}>
                                    <ListItemButton
                                        onClick={() => handleThreadClick(thread)}
                                        sx={{
                                            borderRadius: 1,
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            padding: '8px 12px',
                                            transition: 'all 0.2s',
                                            minHeight: '44px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography
                                                        variant='body2'
                                                        component='div'
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            pr: 1,
                                                            flexGrow: 1,
                                                            fontSize: '0.9rem',
                                                            lineHeight: 1.4
                                                        }}
                                                    >
                                                        {threadTitle}
                                                    </Typography>
                                                    {assistantName && (
                                                        <Chip
                                                            label={assistantName}
                                                            size='small'
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                height: '20px',
                                                                bgcolor: 'rgba(0, 0, 0, 0.06)',
                                                                '& .MuiChip-label': {
                                                                    padding: '0 6px'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                threadDate && (
                                                    <Typography
                                                        variant='body2'
                                                        color='text.secondary'
                                                        component='span'
                                                        sx={{
                                                            float: 'right',
                                                            fontSize: '0.75rem',
                                                            mt: '2px'
                                                        }}
                                                    >
                                                        {formatThreadDate(threadDate)}
                                                    </Typography>
                                                )
                                            }
                                            disableTypography
                                            sx={{ margin: 0 }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            )
                        })}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    )
}
